import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { PlusArchiveTitle } from "scmp-app/components/plus/archive/archive-title";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { factSheetPlusArchiveQuery$key } from "scmp-app/queries/__generated__/factSheetPlusArchiveQuery.graphql";

import { Container, ContentItemContainer, StyledContentItemPlusPrimary } from "./styles";

type Props = {
  className?: string;
  reference: factSheetPlusArchiveQuery$key;
};

export const PlusArchiveFactSheet: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment factSheetPlusArchiveQuery on Query
      @argumentDefinitions(factSheetTypeUuid: { type: "String!" }) {
        pulseArchiveFactSheet: articles(
          filter: { typeIds: [$factSheetTypeUuid] }
          first: 3
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              entityId
              ...plusPrimaryContentItemContent
              ...hooksPlusArticleLinkHandlerArticle
            }
          }
        }
      }
    `,
    reference,
  );
  const { handleOpen: handleOpenDetailedListingPopup } = usePlusDetailedListingPopup();
  const { handleOpen: handleOpenArticlePopup } = usePlusArticlePopup();
  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: () => ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          list_of_articles: (
            content?.pulseArchiveFactSheet.edges.map(edge => edge.node.entityId) ?? []
          ).join(","),
          page_type: "archive",
          widget_name: "fact_sheet",
        },
        subcategory: "widget",
      }),
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "fact_sheet",
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <PlusArchiveTitle
          onClick={() =>
            handleOpenDetailedListingPopup({
              pgtype: "archive",
              type: "fact-sheet",
            })
          }
          title="FactSheet"
        />
        <ContentItemContainer>
          {content?.pulseArchiveFactSheet.edges.map(edge => (
            <StyledContentItemPlusPrimary
              key={edge.node.entityId}
              onClick={() =>
                handleOpenArticlePopup(edge.node, { pageType: "archive", widgetName: "fact_sheet" })
              }
              reference={edge.node}
              withArticleType={false}
            />
          ))}
        </ContentItemContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

PlusArchiveFactSheet.displayName = "PlusArchiveFactSheet";
